import React from 'react';
import PropTypes from 'prop-types';

/**
 * This component determines if the formatted contents of the children exceeds a given height, and if so condenses and
 * truncates the contents of the children.
 *
 * NOTE: Extra whitespace (i.e. newlines) and any html formatting are stripped in the truncated output.
 */
class Truncate extends React.Component {
  constructor(props) {
    super(props);

    this.childRef = undefined;
    this.setChildRef = this.setChildRef.bind(this);
    this.truncateOutput = this.truncateOutput.bind(this);
  }

  componentDidMount() {
    const refStyles = window.getComputedStyle(this.childRef);
    const height = Math.min(parseInt(refStyles.height, 10), this.props.maxHeight);

    const maxLines = Math.floor(height / parseInt(refStyles.lineHeight, 10));
    const shouldTruncate = parseInt(refStyles.height, 10) > this.props.maxHeight;

    if (shouldTruncate) {
      this.truncateOutput(parseInt(refStyles.lineHeight, 10), Math.max(maxLines, 0));
    }
  }

  setChildRef(n) {
    this.childRef = n;
  }

  truncateOutput(lineHeight, maxLines) {
    // HEADS UP - this mutates the copy text itself
    const el = this.childRef;

    // lineHeight and offsetHeight differs across browsers with subpixel rounding, if text is only one line long, make lineHeight equal to height
    let normalizedLineHeight = lineHeight;
    if (el.offsetHeight - lineHeight === 1) {
      normalizedLineHeight = el.offsetHeight;
    }

    const textProp = el.textContent === undefined ? 'innerText' : 'textContent';
    let text = el[textProp];
    text = text.replace(/(?:\r\n|\r|\n)/g, ' ');

    if (maxLines === 0) {
      el[textProp] = '';
      return;
    }

    // determine if text exceeds max height based on maxLines, and if so where to truncate
    const chars = text.split('');
    let upper = chars.length;
    let lower = 0;
    let pivot = 0;
    while (lower < upper) {
      pivot = (lower + upper + 1) >> 1;
      el[textProp] = chars.slice(0, pivot).join('');
      el.insertAdjacentHTML('beforeend', this.props.showMoreCharacter);
      if (el.offsetHeight > normalizedLineHeight * maxLines) upper = pivot - 2;
      else lower = pivot;
    }
    el[textProp] = chars.slice(0, upper).join('');

    // don't display showMoreCharacter if we've truncated whitespace but kept all characters
    if (upper < chars.length) el.insertAdjacentHTML('beforeend', this.props.showMoreCharacter);

    return el[textProp];
  }

  render() {
    return this.props.render(this.props.content, this.setChildRef);
  }
}

Truncate.displayName = 'Truncate';

Truncate.propTypes = {
  content:           PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ __html: PropTypes.string })]).isRequired,
  maxHeight:         PropTypes.number.isRequired,
  render:            PropTypes.func.isRequired,
  showMoreCharacter: PropTypes.string,
};

Truncate.defaultProps = {
  showMoreCharacter: '...',
};
export default Truncate;
